import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Table, Space, Tooltip, Modal, Input, Switch, Select, Alert, Typography, Divider, Statistic, Row, Col, DatePicker } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SyncOutlined, ApiOutlined, CheckCircleOutlined, ExclamationCircleOutlined, WarningOutlined, CreditCardOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { getSessionUser } from "../../../utils/auth/sessionUser";

// API
import { fetchIntegrations, createIntegration, updateIntegration, deleteIntegration, testIntegrationConnection, syncIntegration, getIntegrationSyncStatus, ManualSyncType } from "../../../api/admin/integrationApi";
import { syncAppointmentTypes, syncAppointments, syncSchedules, fullSync } from "../../../api/healthcare/Cliniko/ClinikoSyncApi";
import { fetchTeamMembers } from "../../../api/admin/teamApi";

// Types
import { Integration, IntegrationCreateRequest, IntegrationUpdateRequest, SyncStatusResponse, defaultIntegration } from "../../../types/admin/IntegrationTypes";
import { ClinikoSyncResponse } from "../../../types/appointments/AppointmentSettingsTypes";

import { DataTable, SectionCard, StatusTag } from "../../../components/shared";
import DetailDrawer from "../../../components/shared/DetailDrawer";
import ClinikoImportCard from "./ClinikoImportCard";
import ClinikoFormIssuesCard from "./ClinikoFormIssuesCard";

// Hooks
import { useNotifications } from "../../../context/NotificationContext";
import { validateEmail } from "../../../utils/emailConfigUtils";
import { hasAnyRoleInGroup } from "../../../utils/userPrefs/validatePermissions";
import type { UserUuid } from "../../../types/identity";

dayjs.extend(relativeTime);

const { Text } = Typography;

const { Option } = Select;
const { confirm } = Modal;

/**
 * The import-author picker's pool.
 *
 * `GET /admin/staff` is the sibling Team settings page's own list, `id` being the authentication
 * service's `users.id`, which is exactly the id space `integrations.import_author_id` names. 100 is
 * the server's `per_page` maximum, so the picker holds one page and filters it client-side rather
 * than paging a settings dropdown.
 *
 * ⚠️ **THAT POOL IS NOT "CLINICAL USERS", AND THE ROLE FILTER BELOW IS NOT REDUNDANT.**
 * `/admin/staff` is registered TWICE in the authentication service's `routes/api.php` and Laravel
 * matches in registration order, so `StaffController@index` serves it and `TeamMemberController@index`
 * — the one that filters `user_type = 'clinical'` — is dead code at that path. `StaffController`
 * filters on `is_active` and on holding some role other than `User`/`gene.codes`; it never looks at
 * `user_type` and never excludes `Patient`. Measured against a clinic mid-Cliniko-import it returned
 * 76 users, of which exactly ONE was a practitioner and the rest were that clinic's own patients,
 * created by the import that was running. The id chosen here is written to `patient_notes.author_id`
 * for every imported clinical note, so an unfiltered list lets an operator record a PATIENT as the
 * author of a clinic's entire imported clinical history.
 */
const IMPORT_AUTHOR_PAGE_SIZE = 100;

// "Sync now" is queued (202); sync-status is polled until last_sync_at moves past the value read before dispatch.
const SYNC_POLL_INTERVAL_MS = 5000;

// A first-ever full pull can run for an hour; past this the page stops watching, the run does not stop.
const SYNC_POLL_LIMIT_MS = 15 * 60 * 1000;

interface PendingSync {
	integrationId: number;
	since: string | null;
	startedAt: number;
}

const isSyncInProgress = (error: any): boolean => error?.response?.status === 409 && error?.response?.data?.details?.reason === "sync_in_progress";

interface IntegrationManagementProps {
	companyId: number;
}

interface FormState {
	integration_type: "cliniko" | "stripe";
	api_url: string;
	api_key: string;
	public_key?: string;
	api_username: string;
	webhook_secret?: string;
	/**
	 * Cliniko only, and MANDATORY there — Cliniko blocks api keys whose User-Agent carries no
	 * contact address, so an integration saved without one is a key waiting to be blocked.
	 */
	contact_email: string;
	/** Cliniko only, optional to save — the clinical importers refuse to run until it is set. */
	import_author_id: UserUuid | null;
	sync_patients: boolean;
	sync_practitioners: boolean;
	sync_appointments: boolean;
	/** Cliniko only, off by default — opting a clinic into the hourly form import is a deliberate act. */
	sync_forms: boolean;
	is_active: boolean;
}

/**
 * The form's own field names, as a runtime list.
 *
 * It exists so a server field error can be matched against the form. `keyof FormState` is a
 * compile-time-only thing; a 422 arrives as strings.
 */
const FORM_FIELD_NAMES: readonly string[] = [
	"integration_type",
	"api_url",
	"api_key",
	"public_key",
	"api_username",
	"webhook_secret",
	"contact_email",
	"import_author_id",
	"sync_patients",
	"sync_practitioners",
	"sync_appointments",
	"sync_forms",
	"is_active",
];

const isFormField = (key: string): key is keyof FormState => FORM_FIELD_NAMES.includes(key);

// Older healthcare builds still return the value; it is only ever read as a presence flag.
const hasWebhookSecret = (integration: Integration): boolean =>
	Boolean(integration.has_webhook_secret || integration.configuration?.webhook_secret);

/**
 * Split a failed save into what belongs ON a field and what does not.
 *
 * Laravel answers a rejected create/update with `422 { message, errors: { field: string[] } }`.
 * A missing or malformed `contact_email` is the common case here and it MUST land on the input —
 * an operator reading "the contact email field is required" in a corner toast, with nothing marked
 * on a form of eight inputs, has to guess which one. Anything that maps to no field on this form
 * is returned as a message so it still reaches them rather than being swallowed.
 */
const splitSaveError = (error: any): { fields: Partial<Record<keyof FormState, string>>; messages: string[] } => {
	const serverErrors = error?.response?.data?.errors as Record<string, string[]> | undefined;
	const fields: Partial<Record<keyof FormState, string>> = {};
	const messages: string[] = [];

	if (!serverErrors) {
		return { fields, messages };
	}

	Object.entries(serverErrors).forEach(([key, fieldMessages]) => {
		const text = (Array.isArray(fieldMessages) ? fieldMessages : [fieldMessages]).join(" ");
		if (isFormField(key)) {
			fields[key] = text;
		} else {
			messages.push(text);
		}
	});

	return { fields, messages };
};

const IntegrationManagement: React.FC<IntegrationManagementProps> = ({ companyId }) => {
	const [integrations, setIntegrations] = useState<Integration[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
	const [editingIntegration, setEditingIntegration] = useState<Integration | null>(null);
	const [testingConnection, setTestingConnection] = useState<boolean>(false);
	const [syncing, setSyncing] = useState<boolean>(false);
	const [syncDrawerVisible, setSyncDrawerVisible] = useState<boolean>(false);
	const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
	const [syncingAppointments, setSyncingAppointments] = useState<boolean>(false);
	const [appointmentSyncType, setAppointmentSyncType] = useState<string>("");
	const [sinceDate, setSinceDate] = useState<Dayjs | null>(null);
	const [lastAppointmentSyncResult, setLastAppointmentSyncResult] = useState<ClinikoSyncResponse | null>(null);
	const [pendingSync, setPendingSync] = useState<PendingSync | null>(null);

	// Form state
	const [formState, setFormState] = useState<FormState>({
		integration_type: "cliniko",
		api_url: "https://api.au1.cliniko.com/v1",
		api_key: "",
		public_key: "",
		api_username: "",
		webhook_secret: "",
		contact_email: "",
		import_author_id: null,
		sync_patients: true,
		sync_practitioners: true,
		sync_appointments: false,
		sync_forms: false,
		is_active: true,
	});

	const [validationErrors, setValidationErrors] = useState<Partial<Record<keyof FormState, string>>>({});

	const { showSuccess, showError, showInfo } = useNotifications();
	const user = getSessionUser();

	// The import-author pool. Loaded only while the Cliniko form is open — this list is the Team
	// page's own request and there is no reason to make it on every visit to the Integrations tab.
	const importAuthorsQuery = useQuery({
		queryKey: ["integration-import-authors"],
		queryFn: () => fetchTeamMembers(1, IMPORT_AUTHOR_PAGE_SIZE),
		enabled: drawerVisible && formState.integration_type === "cliniko",
	});

	// Clinical staff only — the pool this is filtered from contains the clinic's patients; see
	// IMPORT_AUTHOR_PAGE_SIZE above for why, and for what selecting one of them would write.
	// The CLINICAL role group asked of another account's record — session permissions cannot answer for someone else.
	// A record whose `roles` are missing is not offered — nothing may become a note author by default.
	const importAuthorOptions = (importAuthorsQuery.data?.data ?? [])
		.filter((member) => hasAnyRoleInGroup("CLINICAL", member))
		.map((member) => ({
			value: member.id,
			label: `${member.first_name ?? ""} ${member.last_name ?? ""}`.trim() || member.email,
		}));

	const syncStatusQuery = useQuery({
		queryKey: ["integration-sync-status", pendingSync?.integrationId, pendingSync?.startedAt],
		queryFn: () => getIntegrationSyncStatus(pendingSync!.integrationId, companyId),
		enabled: pendingSync !== null,
		refetchInterval: () => (pendingSync ? SYNC_POLL_INTERVAL_MS : false),
	});

	useEffect(() => {
		loadIntegrations();
	}, [companyId]);

	useEffect(() => {
		const status = syncStatusQuery.data;
		if (!pendingSync || !status) return;

		if ((status.last_sync_at ?? null) !== pendingSync.since) {
			setPendingSync(null);
			finishSync(status);
		} else if (Date.now() - pendingSync.startedAt > SYNC_POLL_LIMIT_MS) {
			setPendingSync(null);
			setSyncing(false);
			showInfo("The sync is still running in the background. Its results will appear here when it finishes.");
		}
	}, [syncStatusQuery.dataUpdatedAt]);

	const loadIntegrations = async () => {
		setLoading(true);
		try {
			const data = await fetchIntegrations(companyId);
			setIntegrations(data);
		} catch (error) {
			showError("Failed to load integrations");
		} finally {
			setLoading(false);
		}
	};

	const resetFormState = () => {
		setFormState({
			integration_type: "cliniko",
			api_url: "https://api.au1.cliniko.com/v1",
			api_key: "",
			api_username: "",
			webhook_secret: "",
			contact_email: "",
			import_author_id: null,
			sync_patients: true,
			sync_practitioners: true,
			sync_appointments: false,
			sync_forms: false,
			is_active: true,
		});
		setValidationErrors({});
	};

	const openDrawer = (integration?: Integration) => {
		if (integration) {
			setEditingIntegration(integration);
			setFormState({
				integration_type: integration.integration_type as "cliniko" | "stripe",
				api_url: integration.api_url || (integration.integration_type === "cliniko" ? "https://api.au1.cliniko.com/v1" : ""),
				api_key: "",
				api_username: "",
				webhook_secret: "",
				// Both are returned on read (unlike the write-only credentials above), so they
				// repopulate and an edit that touches neither round-trips them unchanged.
				contact_email: integration.contact_email || "",
				import_author_id: integration.import_author_id ?? null,
				sync_patients: integration.sync_patients,
				sync_practitioners: integration.sync_practitioners,
				sync_appointments: integration.sync_appointments,
				sync_forms: integration.sync_forms,
				is_active: integration.is_active,
			});
		} else {
			setEditingIntegration(null);
			resetFormState();
		}
		setDrawerVisible(true);
	};

	const closeDrawer = () => {
		setDrawerVisible(false);
		setEditingIntegration(null);
		resetFormState();
	};

	const updateFormField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
		setFormState((prev) => ({ ...prev, [field]: value }));
		// Clear validation error for this field
		if (validationErrors[field]) {
			setValidationErrors((prev) => {
				const newErrors = { ...prev };
				delete newErrors[field];
				return newErrors;
			});
		}
	};

	const validateForm = (): boolean => {
		const errors: Partial<Record<keyof FormState, string>> = {};

		if (formState.integration_type === "cliniko") {
			if (!formState.api_url) {
				errors.api_url = "API URL is required";
			} else if (!formState.api_url.startsWith("http")) {
				errors.api_url = "Invalid URL format";
			}

			// Refused here as well as by the server, because the consequence of a Cliniko
			// integration saved without one is not a failed save — it is a blocked api key.
			if (!formState.contact_email.trim()) {
				errors.contact_email = "Contact email is required — Cliniko blocks API keys whose requests carry no contact address";
			} else if (!validateEmail(formState.contact_email.trim())) {
				errors.contact_email = "Enter a valid email address";
			}
		}

		if (!editingIntegration && !formState.api_key) {
			errors.api_key = "API Key is required";
		}

		if (formState.integration_type === "stripe") {
			if (formState.api_key && !formState.api_key.startsWith("sk_")) {
				errors.api_key = "Stripe API key must start with sk_";
			}
		}

		setValidationErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleSubmit = async () => {
		if (!validateForm()) {
			return;
		}

		setLoading(true);
		try {
			if (editingIntegration) {
				const updateData: IntegrationUpdateRequest = {
					sync_patients: formState.sync_patients,
					sync_practitioners: formState.sync_practitioners,
					sync_appointments: formState.sync_appointments,
					sync_forms: formState.sync_forms,
					is_active: formState.is_active,
				};

				if (formState.api_key) {
					updateData.api_key = formState.api_key;
				}
				if(formState.public_key) {
					updateData.public_key = formState.public_key;
				}
				if (formState.api_url !== editingIntegration.api_url) {
					updateData.api_url = formState.api_url;
				}
				if (formState.api_username) {
					updateData.api_username = formState.api_username;
				}
				// Sent unconditionally on a Cliniko update, unlike the write-only credentials above:
				// `contact_email` is mandatory so there is nothing to preserve by omitting it, and
				// `import_author_id` must be able to travel as `null` — an "only if truthy" guard
				// would make the picker's clear button silently do nothing.
				if (formState.integration_type === "cliniko") {
					updateData.contact_email = formState.contact_email.trim();
					updateData.import_author_id = formState.import_author_id;
				}
				if (formState.webhook_secret) {
					updateData.configuration = {
						webhook_secret: formState.webhook_secret,
					};
				}

				await updateIntegration(editingIntegration.id, updateData);
				showSuccess("Integration updated successfully");
			} else {
				const createData: IntegrationCreateRequest = {
					integration_type: formState.integration_type,
					api_key: formState.api_key,
					public_key: formState.public_key,
					api_url: formState.api_url,
					api_username: formState.integration_type === "cliniko" ? formState.api_username : undefined,
					contact_email: formState.integration_type === "cliniko" ? formState.contact_email.trim() : undefined,
					import_author_id: formState.integration_type === "cliniko" ? formState.import_author_id : undefined,
					sync_patients: formState.sync_patients,
					sync_practitioners: formState.sync_practitioners,
					sync_appointments: formState.sync_appointments,
					sync_forms: formState.sync_forms,
				};

				if (formState.webhook_secret) {
					createData.configuration = {
						webhook_secret: formState.webhook_secret,
					};
				}

				await createIntegration(createData);
				showSuccess("Integration created successfully");
			}

			await loadIntegrations();
			closeDrawer();
		} catch (error: any) {
			const { fields, messages } = splitSaveError(error);

			if (Object.keys(fields).length > 0) {
				setValidationErrors((prev) => ({ ...prev, ...fields }));
			}

			// A field error is shown ON its field and NOT repeated as a toast — the drawer stays
			// open, so the operator sees the marked input. Only what could not be placed on a
			// field falls through to the toast.
			if (messages.length > 0) {
				showError(messages.join(" "));
			} else if (Object.keys(fields).length === 0) {
				showError(error.response?.data?.message || "Failed to save integration");
			}
		} finally {
			setLoading(false);
		}
	};

	const handleTestConnection = async (integration: Integration) => {
		setTestingConnection(true);
		try {
			const result = await testIntegrationConnection(integration.id, companyId);
			if (result.success) {
				showSuccess("Connection successful!");
			} else {
				showError(result.error || "Connection failed");
			}
		} catch (error: any) {
			showError(error.response?.data?.message || "Connection test failed");
		} finally {
			setTestingConnection(false);
		}
	};

	const finishSync = async (status: SyncStatusResponse) => {
		const results = status.sync_statistics?.last_sync_results;

		if (status.last_sync_error) {
			showError(status.last_sync_error);
		} else if (results) {
			showSuccess(`Sync completed! Patients: ${results.patients.synced} synced, ${results.patients.errors} errors. Practitioners: ${results.practitioners.synced} synced, ${results.practitioners.errors} errors.`);
		} else {
			showSuccess("Sync completed!");
		}

		setSyncing(false);
		await loadIntegrations();
		setSyncDrawerVisible(false);
	};

	const handleSync = async (syncType: ManualSyncType) => {
		if (!selectedIntegration) return;

		const integrationId = selectedIntegration.id;
		setSyncing(true);
		try {
			const before = await getIntegrationSyncStatus(integrationId, companyId);
			await syncIntegration(integrationId, companyId, syncType);
			showInfo("Sync queued. The results will appear here when it finishes.");
			setPendingSync({ integrationId, since: before.last_sync_at ?? null, startedAt: Date.now() });
		} catch (error: any) {
			setSyncing(false);
			if (isSyncInProgress(error)) {
				showError("A Cliniko sync is already queued or running. Try again when it finishes.");
			} else {
				showError(error.response?.data?.message || "Sync failed");
			}
		}
	};

	const handleDelete = (integration: Integration) => {
		confirm({
			title: "Confirm Deletion",
			content: `Are you sure you want to delete the ${integration.integration_type} integration?`,
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					await deleteIntegration(integration.id, companyId);
					showSuccess("Integration deleted successfully");
					await loadIntegrations();
				} catch (error) {
					showError("Failed to delete integration");
				}
			},
		});
	};

	const openSyncDrawer = (integration: Integration) => {
		setSelectedIntegration(integration);
		setSyncDrawerVisible(true);
	};

	const handleAppointmentSync = async (type: "types" | "appointments" | "schedules" | "full") => {
		setSyncingAppointments(true);
		setAppointmentSyncType(type);
		setLastAppointmentSyncResult(null);

		try {
			let result: ClinikoSyncResponse;

			switch (type) {
				case "types":
					result = await syncAppointmentTypes();
					break;
				case "appointments":
					result = await syncAppointments(sinceDate?.format("YYYY-MM-DD"));
					break;
				case "schedules":
					result = await syncSchedules();
					break;
				case "full":
					result = await fullSync(sinceDate?.format("YYYY-MM-DD"));
					break;
			}

			setLastAppointmentSyncResult(result);

			if (result.success) {
				showSuccess(result.message || "Sync completed successfully");
			} else {
				showError(result.error || "Sync completed with errors");
			}
		} catch (error: any) {
			showError(error.response?.data?.message || "Sync failed");
		} finally {
			setSyncingAppointments(false);
			setAppointmentSyncType("");
		}
	};

	const getStatusBadge = (integration: Integration) => {
		if (!integration.is_active) {
			return <StatusTag status="inactive" />;
		}
		if (integration.last_sync_error) {
			return <StatusTag status="error" />;
		}
		if (integration.last_successful_sync_at) {
			return <StatusTag status="active" />;
		}
		return <StatusTag status="warning" label="Never Synced" />;
	};

	const getIntegrationIcon = (type: string) => {
		return type === "stripe" ? <CreditCardOutlined style={{ fontSize: 18, color: "var(--mg-primary)" }} /> : <ApiOutlined style={{ fontSize: 18, color: "var(--mg-info)" }} />;
	};

	const columns = [
		{
			title: "Integration",
			dataIndex: "integration_type",
			key: "integration_type",
			render: (type: string) => (
				<Space>
					{getIntegrationIcon(type)}
					<Text strong style={{ textTransform: "capitalize" }}>
						{type}
					</Text>
				</Space>
			),
		},
		{
			title: "Sync Settings",
			key: "sync_settings",
			render: (_: any, integration: Integration) =>
				integration.integration_type === "cliniko" ? (
					<Space size={4}>
						{integration.sync_patients && <span style={{ background: "var(--mg-info-bg)", color: "var(--mg-info)", border: "1px solid var(--mg-info-border)", borderRadius: 4, padding: "1px 6px", fontSize: 12 }}>Patients</span>}
						{integration.sync_practitioners && <span style={{ background: "var(--mg-success-bg)", color: "var(--mg-success)", border: "1px solid var(--mg-success-border)", borderRadius: 4, padding: "1px 6px", fontSize: 12 }}>Practitioners</span>}
						{integration.sync_appointments && <span style={{ background: "var(--mg-primary-bg)", color: "var(--mg-primary)", border: "1px solid var(--mg-primary-border)", borderRadius: 4, padding: "1px 6px", fontSize: 12 }}>Appointments</span>}
						{integration.sync_forms && <span style={{ background: "var(--mg-warning-bg)", color: "var(--mg-warning)", border: "1px solid var(--mg-warning-border)", borderRadius: 4, padding: "1px 6px", fontSize: 12 }}>Forms</span>}
					</Space>
				) : (
					<span style={{ background: "var(--mg-warning-bg)", color: "var(--mg-warning)", border: "1px solid var(--mg-warning-border)", borderRadius: 4, padding: "1px 6px", fontSize: 12 }}>Payment Processing</span>
				),
		},
		{
			title: "Status",
			key: "status",
			render: (_: any, integration: Integration) => getStatusBadge(integration),
		},
		{
			title: "Last Sync",
			dataIndex: "last_successful_sync_at",
			key: "last_sync",
			render: (date: string | null, integration: Integration) => (integration.integration_type === "cliniko" ? (date ? dayjs(date).fromNow() : "Never") : <Text type="secondary">N/A</Text>),
		},
		{
			title: "",
			key: "actions",
			width: 140,
			render: (_: any, integration: Integration) => (
				<Space size={2}>
					<Tooltip title="Test Connection">
						<Button type="text" icon={<CheckCircleOutlined />} size="small" loading={testingConnection} onClick={(e) => { e.stopPropagation(); handleTestConnection(integration); }} />
					</Tooltip>
					{integration.integration_type === "cliniko" && (
						<Tooltip title="Sync Now">
							<Button type="text" icon={<SyncOutlined />} size="small" disabled={!integration.is_active} onClick={(e) => { e.stopPropagation(); openSyncDrawer(integration); }} />
						</Tooltip>
					)}
					<Tooltip title="Edit">
						<Button type="text" icon={<EditOutlined />} size="small" onClick={(e) => { e.stopPropagation(); openDrawer(integration); }} />
					</Tooltip>
					<Tooltip title="Delete">
						<Button type="text" danger icon={<DeleteOutlined />} size="small" onClick={(e) => { e.stopPropagation(); handleDelete(integration); }} />
					</Tooltip>
				</Space>
			),
		},
	];

	// The clinic's Cliniko integration, if it has one. The full-import card is gated on the
	// integration EXISTING, not on its sync toggles — a clinic migrating OFF Cliniko has every
	// reason to have those switched off, and the server gates the import routes the same way
	// (`ClinikoIntegration::hasCredentials()` alone).
	const clinikoIntegration = integrations.find((integration) => integration.integration_type === "cliniko") ?? null;

	return (
		<>
		<SectionCard
			title="External Integrations"
			subtitle="Connect external systems like Cliniko for patient data or Stripe for payments"
			extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => openDrawer()}>Add Integration</Button>}
			noPadding
		>
			<DataTable
				columns={columns}
				dataSource={integrations}
				rowKey="id"
				loading={loading && !drawerVisible}
				pagination={false}
				emptyTitle="No integrations configured"
				emptyDescription="Connect external systems to sync patient data or process payments"
				emptyAction={{ label: "Add your first integration", onClick: () => openDrawer() }}
				bare
			/>

			{integrations.length > 0 && integrations[0].integration_type === "cliniko" && integrations[0].sync_statistics && (
				<div style={{ padding: "0 16px 16px" }}>
					<Divider style={{ margin: "0 0 16px" }} />
					<Row gutter={16}>
										<Col span={4}>
											<Statistic title="Patients" value={integrations[0].sync_statistics.last_sync_results?.patients.synced || 0} prefix={<CheckCircleOutlined style={{ color: "var(--mg-success)" }} />} />
										</Col>
										<Col span={4}>
											<Statistic title="Practitioners" value={integrations[0].sync_statistics.last_sync_results?.practitioners.synced || 0} prefix={<CheckCircleOutlined style={{ color: "var(--mg-success)" }} />} />
										</Col>
										<Col span={4}>
											<Statistic title="Appointments" value={integrations[0].sync_statistics.last_sync_results?.appointments.synced || 0} prefix={<CheckCircleOutlined style={{ color: "var(--mg-success)" }} />} />
										</Col>
										<Col span={4}>
											<Statistic title="Appointment Types" value={integrations[0].sync_statistics.last_sync_results?.appointment_types.synced || 0} prefix={<CheckCircleOutlined style={{ color: "var(--mg-success)" }} />} />
										</Col>
										<Col span={4}>
											<Statistic title="Schedules" value={integrations[0].sync_statistics.last_sync_results?.schedules.synced || 0} prefix={<CheckCircleOutlined style={{ color: "var(--mg-success)" }} />} />
										</Col>
										<Col span={4}>
											<Statistic title="Sync Errors" value={integrations[0].sync_retry_count} prefix={<ExclamationCircleOutlined style={{ color: "var(--mg-danger)" }} />} />
										</Col>
									</Row>
				</div>
			)}

			{/* Configuration Drawer */}
			<DetailDrawer
				title={editingIntegration ? "Edit Integration" : "Add Integration"}
				open={drawerVisible}
				onClose={closeDrawer}
				width="70vw"
				headerActions={
					<Button type="primary" onClick={handleSubmit} loading={loading}>
						{editingIntegration ? "Update" : "Create"}
					</Button>
				}
			>
				<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
					{!editingIntegration && (
						<div>
							<Text strong style={{ display: "block", marginBottom: 8 }}>
								Integration Type <span style={{ color: "#ff4d4f" }}>*</span>
							</Text>
							<Select
								value={formState.integration_type}
								onChange={(value) => {
									updateFormField("integration_type", value);
									if (value === "stripe") {
										updateFormField("api_url", "");
										updateFormField("sync_patients", false);
										updateFormField("sync_practitioners", false);
										updateFormField("sync_appointments", false);
										updateFormField("sync_forms", false);
									}
								}}
								style={{ width: "100%" }}

							>
								<Option value="cliniko">
									<Space>
										<ApiOutlined />
										Cliniko (Patient Data)
									</Space>
								</Option>
								<Option value="stripe">
									<Space>
										<CreditCardOutlined />
										Stripe (Payments)
									</Space>
								</Option>
							</Select>
						</div>
					)}

					<Alert message="API Credentials" description="Your API credentials are encrypted and stored securely. They are never exposed in API responses." type="info" showIcon />

					<div>
						<Text strong style={{ display: "block", marginBottom: 8 }}>
							API URL <span style={{ color: "#ff4d4f" }}>*</span>
						</Text>
						<Input value={formState.api_url} onChange={(e) => updateFormField("api_url", e.target.value)} placeholder="https://api.au1.cliniko.com/v1" status={validationErrors.api_url ? "error" : ""} />
						{validationErrors.api_url && (
							<Text type="danger" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
								{validationErrors.api_url}
							</Text>
						)}
					</div>

					<div>
						<Text strong style={{ display: "block", marginBottom: 8 }}>
							API Key {!editingIntegration && <span style={{ color: "#ff4d4f" }}>*</span>}
						</Text>
						<Input.Password
							value={formState.api_key}
							onChange={(e) => updateFormField("api_key", e.target.value)}
							placeholder={editingIntegration ? "Leave empty to keep current key" : formState.integration_type === "stripe" ? "sk_..." : "Your API key"}

							status={validationErrors.api_key ? "error" : ""}
						/>
						{editingIntegration && (
							<Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
								Leave empty to keep the current API key
							</Text>
						)}
						{validationErrors.api_key && (
							<Text type="danger" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
								{validationErrors.api_key}
							</Text>
						)}
					</div>

					<div>
						<Text strong style={{ display: "block", marginBottom: 8 }}>
							Public Key {!editingIntegration && <span style={{ color: "#ff4d4f" }}>*</span>}
						</Text>
						<Input.Password
							value={formState.public_key}
							onChange={(e) => updateFormField("public_key", e.target.value)}
							placeholder={editingIntegration ? "Leave empty to keep current key" : formState.integration_type === "stripe" ? "pk_..." : "Your API key"}

							status={validationErrors.public_key ? "error" : ""}
						/>
					</div>

					{formState.integration_type === "stripe" && (
						<div>
							<Text strong style={{ display: "block", marginBottom: 8 }}>
								Webhook Secret
							</Text>
							<Input.Password
								value={formState.webhook_secret}
								onChange={(e) => updateFormField("webhook_secret", e.target.value)}
								placeholder={editingIntegration && hasWebhookSecret(editingIntegration) ? "Leave blank to keep the current secret" : "whsec_..."}
								autoComplete="new-password"
							/>
							{editingIntegration && (
								<Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
									Current secret: {hasWebhookSecret(editingIntegration) ? "Set" : "Not set"}
								</Text>
							)}
							<Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
								Used to verify webhook signatures from Stripe
							</Text>
						</div>
					)}

					{formState.integration_type === "cliniko" && (
						<>
							<div>
								<Text strong style={{ display: "block", marginBottom: 8 }}>
									API Username (Optional)
								</Text>
								<Input value={formState.api_username} onChange={(e) => updateFormField("api_username", e.target.value)} placeholder="For Basic Auth (if required)" />
							</div>

							<div>
								<Text strong style={{ display: "block", marginBottom: 8 }}>
									Cliniko Contact Email <span style={{ color: "#ff4d4f" }}>*</span>
								</Text>
								<Input
									value={formState.contact_email}
									onChange={(e) => updateFormField("contact_email", e.target.value)}
									placeholder="practice.manager@yourclinic.com.au"
									status={validationErrors.contact_email ? "error" : ""}
								/>
								{/* Stated on the form, not in a tooltip: a clinic admin who does not know WHY
								    this is mandatory will put a plausible-looking unmonitored address in it. */}
								<Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
									Required. Cliniko blocks API keys whose requests carry no contact address, so this address is sent with
									every Cliniko request made on this clinic's behalf. Use a real monitored mailbox — Cliniko may use it to
									contact the clinic about its API usage.
								</Text>
								{validationErrors.contact_email && (
									<Text type="danger" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
										{validationErrors.contact_email}
									</Text>
								)}
							</div>

							<div>
								<Text strong style={{ display: "block", marginBottom: 8 }}>
									Import Author (Optional)
								</Text>
								<Select
									value={formState.import_author_id ?? undefined}
									onChange={(value) => updateFormField("import_author_id", value ?? null)}
									options={importAuthorOptions}
									loading={importAuthorsQuery.isLoading}
									allowClear
									showSearch
									optionFilterProp="label"
									placeholder="Select a practitioner"
									style={{ width: "100%" }}
									status={validationErrors.import_author_id ? "error" : ""}
								/>
								<Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
									The practitioner that imported clinical records are filed under when their Cliniko clinician has no Xoe
									account. The integration saves without it, but the treatment-note and form importers refuse to run until
									it is set — set it before starting a clinical import.
								</Text>
								{importAuthorsQuery.isError && (
									<Text type="warning" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
										The team list could not be loaded, so there is nothing to pick from. Reopen this drawer to retry.
									</Text>
								)}
								{/* An empty dropdown here looks like a loading fault, and the reason is never
								    guessable: the list loaded fine and everything in it was filtered out. A new
								    clinic starts in exactly this state. */}
								{importAuthorsQuery.isSuccess && importAuthorOptions.length === 0 && (
									<Text type="warning" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
										This clinic has no practitioner account, so there is nothing to select. Add one under Settings → Team
										Management (a patient account cannot be the author of clinical records), then reopen this drawer.
									</Text>
								)}
								{validationErrors.import_author_id && (
									<Text type="danger" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
										{validationErrors.import_author_id}
									</Text>
								)}
							</div>

							<Divider style={{ margin: "8px 0" }} />

							<div>
								<Text strong style={{ display: "block", marginBottom: 8 }}>
									Sync Configuration
								</Text>
								<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
									Select what data to synchronize with this integration
								</Text>

								<Space direction="vertical" style={{ width: "100%" }}>
									<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
										<Text>Sync Patients</Text>
										<Switch checked={formState.sync_patients} onChange={(checked) => updateFormField("sync_patients", checked)} />
									</div>

									<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
										<Text>Sync Practitioners</Text>
										<Switch checked={formState.sync_practitioners} onChange={(checked) => updateFormField("sync_practitioners", checked)} />
									</div>

									<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
										<Text>Sync Appointments</Text>
										<Switch checked={formState.sync_appointments} onChange={(checked) => updateFormField("sync_appointments", checked)} />
									</div>

									<div>
										<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
											<Text>Sync Forms</Text>
											<Switch checked={formState.sync_forms} onChange={(checked) => updateFormField("sync_forms", checked)} />
										</div>
										<Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
											Hourly import of Cliniko patient forms and completed responses. Requires an import author.
										</Text>
									</div>
								</Space>
							</div>
						</>
					)}

					<Divider style={{ margin: "8px 0" }} />

					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<Text strong>Active</Text>
						<Switch checked={formState.is_active} onChange={(checked) => updateFormField("is_active", checked)} />
					</div>
				</div>

				{editingIntegration && editingIntegration.last_sync_error && <Alert message="Last Sync Error" description={editingIntegration.last_sync_error} type="error" showIcon style={{ marginTop: 16 }} />}
			</DetailDrawer>

			{/* Sync Drawer - Only for Cliniko */}
			<DetailDrawer
				title="Sync Integration"
				open={syncDrawerVisible}
				onClose={() => setSyncDrawerVisible(false)}
				width={600}
			>
				{selectedIntegration && selectedIntegration.integration_type === "cliniko" && (
					<div>
						<Alert message={`Syncing: ${selectedIntegration.integration_type}`} description="Choose what data to synchronize. This process may take a few minutes." type="info" showIcon style={{ marginBottom: 24 }} />

						<Button type="primary" icon={<SyncOutlined />} block loading={syncing} disabled={syncing} onClick={() => handleSync("all")} style={{ marginBottom: 16 }}>
							Sync All Data
						</Button>

						<Divider>User Data</Divider>

						<Space direction="vertical" style={{ width: "100%" }} size="large">
							<Button icon={<SyncOutlined />} block loading={syncing} disabled={syncing} onClick={() => handleSync("patients")}>
								Sync Patients Only
							</Button>

							<Button icon={<SyncOutlined />} block loading={syncing} disabled={syncing} onClick={() => handleSync("practitioners")}>
								Sync Practitioners Only
							</Button>

							<Divider style={{ margin: 0 }}>Appointment Data</Divider>

							<Button icon={<SyncOutlined spin={syncingAppointments && appointmentSyncType === "types"} />} block loading={syncing} disabled={syncing} onClick={() => handleAppointmentSync("types")}>
								Appointment Types
							</Button>

							<Button icon={<SyncOutlined spin={syncingAppointments && appointmentSyncType === "appointments"} />} block loading={syncing} disabled={syncing} onClick={() => handleAppointmentSync("appointments")}>
								Sync Appointments
							</Button>
							<Button icon={<SyncOutlined spin={syncingAppointments && appointmentSyncType === "schedules"} />} block loading={syncing} disabled={syncing} onClick={() => handleAppointmentSync("schedules")}>
								Practitioner Schedules
							</Button>
						</Space>

						{selectedIntegration.sync_statistics?.last_sync_results && (
							<>
								<Divider />
								<div>
									<Text strong style={{ display: "block", marginBottom: 12 }}>
										Last Integration Sync Results
									</Text>
									<Space direction="vertical" style={{ width: "100%" }}>
										<div>
											<Text type="secondary">Patients:</Text> {selectedIntegration.sync_statistics.last_sync_results.patients.synced} synced, {selectedIntegration.sync_statistics.last_sync_results.patients.errors} errors
										</div>
										<div>
											<Text type="secondary">Practitioners:</Text> {selectedIntegration.sync_statistics.last_sync_results.practitioners.synced} synced, {selectedIntegration.sync_statistics.last_sync_results.practitioners.errors}{" "}
											errors
										</div>
										<div>
											<Text type="secondary">Appointments:</Text> {selectedIntegration.sync_statistics.last_sync_results.appointments?.synced || 0} synced,{" "}
											{selectedIntegration.sync_statistics.last_sync_results.appointments?.errors || 0} errors
										</div>
										<div>
											<Text type="secondary">Appointment Types:</Text> {selectedIntegration.sync_statistics.last_sync_results.appointment_types?.synced || 0} synced,{" "}
											{selectedIntegration.sync_statistics.last_sync_results.appointment_types?.errors || 0} errors
										</div>
										<div>
											<Text type="secondary">Schedules:</Text> {selectedIntegration.sync_statistics.last_sync_results.schedules?.synced || 0} synced, {selectedIntegration.sync_statistics.last_sync_results.schedules?.errors || 0}{" "}
											errors
										</div>
										<div>
											<Text type="secondary">Last synced:</Text> {selectedIntegration.last_successful_sync_at ? dayjs(selectedIntegration.last_successful_sync_at).fromNow() : "Never"}
										</div>
									</Space>
								</div>
							</>
						)}

						{lastAppointmentSyncResult && (
							<>
								<Divider />
								<div>
									<Text strong style={{ display: "block", marginBottom: 12 }}>
										Last Appointment Sync Results
									</Text>
									<Space direction="vertical" style={{ width: "100%" }} size="middle">
										<div>
							<StatusTag status={lastAppointmentSyncResult.success ? "active" : "error"} label={lastAppointmentSyncResult.success ? "Sync Completed" : "Sync Failed"} />
							{lastAppointmentSyncResult.message && <Text style={{ display: "block", marginTop: 8 }}>{lastAppointmentSyncResult.message}</Text>}
						</div>
										{lastAppointmentSyncResult.data && (
											<Row gutter={16}>
												{lastAppointmentSyncResult.data.appointment_types && (
													<Col span={8}>
														<Statistic
															title="Appointment Types"
															value={lastAppointmentSyncResult.data.appointment_types.synced}
															suffix={
																<Space>
																	<CheckCircleOutlined style={{ color: "var(--mg-success)" }} />
																	{lastAppointmentSyncResult.data.appointment_types.errors > 0 && (
																		<>
																			| <WarningOutlined style={{ color: "var(--mg-warning)" }} /> {lastAppointmentSyncResult.data.appointment_types.errors}
																		</>
																	)}
																</Space>
															}
														/>
													</Col>
												)}

												{lastAppointmentSyncResult.data.schedules && (
													<Col span={8}>
														<Statistic
															title="Schedules"
															value={lastAppointmentSyncResult.data.schedules.synced}
															suffix={
																<Space>
																	<CheckCircleOutlined style={{ color: "var(--mg-success)" }} />
																	{lastAppointmentSyncResult.data.schedules.errors > 0 && (
																		<>
																			| <WarningOutlined style={{ color: "var(--mg-warning)" }} /> {lastAppointmentSyncResult.data.schedules.errors}
																		</>
																	)}
																</Space>
															}
														/>
													</Col>
												)}

												{lastAppointmentSyncResult.data.appointments && (
													<Col span={8}>
														<Statistic
															title="Appointments"
															value={lastAppointmentSyncResult.data.appointments.synced}
															suffix={
																<Space>
																	<CheckCircleOutlined style={{ color: "var(--mg-success)" }} />
																	{lastAppointmentSyncResult.data.appointments.errors > 0 && (
																		<>
																			| <WarningOutlined style={{ color: "var(--mg-warning)" }} /> {lastAppointmentSyncResult.data.appointments.errors}
																		</>
																	)}
																</Space>
															}
														/>
													</Col>
												)}
											</Row>
										)}

										{lastAppointmentSyncResult.error && <Alert message="Error" description={lastAppointmentSyncResult.error} type="error" showIcon />}
									</Space>
								</div>
							</>
						)}
					</div>
				)}
			</DetailDrawer>
		</SectionCard>

		{clinikoIntegration && <ClinikoImportCard integration={clinikoIntegration} />}
		{clinikoIntegration && <ClinikoFormIssuesCard />}
		</>
	);
};

export default IntegrationManagement;